import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import { getPaginatedCustomers } from "../../services/contractorService";
import { formatIntegerAmount } from "../../utils/CommonFunction";
import { renderCurrencyIconColored } from "../../utils/CurrencyUtils";

const StyledHeading = styled(Typography)(() => ({
  fontSize: "24px",
  fontWeight: 600,
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  letterSpacing: 0,
}));

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  fontFamily: "Inter Tight",
}));

const PILL_COLORS = [
  { bg: "#EEF2FF", fg: "#4D6BF6" },
  { bg: "#ECFDF5", fg: "#16A34A" },
  { bg: "#FEF3C7", fg: "#D97706" },
  { bg: "#FCE7F3", fg: "#DB2777" },
  { bg: "#E0E7FF", fg: "#4338CA" },
  { bg: "#FEE2E2", fg: "#DC2626" },
];

const pillFor = (text) => {
  const s = (text || "?").trim();
  const ch = s.charAt(0).toUpperCase() || "?";
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash + s.charCodeAt(i)) % PILL_COLORS.length;
  }
  return { letter: ch, ...PILL_COLORS[hash] };
};

const PER_PAGE_OPTIONS = [10, 20, 50];

const CountPill = ({ value }) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return (
      <Typography
        component="span"
        sx={{ fontSize: 13, color: "#9CA3AF", fontFamily: "Inter Tight" }}
      >
        -
      </Typography>
    );
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 28,
        height: 22,
        padding: "0 8px",
        borderRadius: "999px",
        backgroundColor: "#EEF2FF",
        color: "#4D6BF6",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "Inter Tight",
      }}
    >
      {n}
    </Box>
  );
};

const ProjectValueCell = ({ value, align = "right" }) => {
  const formatted = formatIntegerAmount(value);
  if (!formatted) {
    return (
      <Typography
        component="span"
        sx={{ fontSize: 13, color: "#9CA3AF", fontFamily: "Inter Tight" }}
      >
        -
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        gap: 0.5,
        fontSize: 13,
        color: "#0D0D0E",
        fontFamily: "Inter Tight",
        fontWeight: 500,
      }}
    >
      {renderCurrencyIconColored("black-xs-small")}
      {formatted}
    </Box>
  );
};

const Customers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setSearch(value);
      }, 350),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel?.(), [debouncedSetSearch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getPaginatedCustomers({ search, page, perPage });
        if (cancelled) return;
        const list =
          (Array.isArray(res?.data) && res.data) ||
          res?.data?.customers ||
          res?.customers ||
          [];
        const totalCount =
          res?.data?.total ??
          res?.total ??
          res?.data?.count ??
          res?.count ??
          list.length;
        setItems(list);
        setTotal(totalCount);
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load customers", e);
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search, page, perPage]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / perPage));
  const showEmpty = !loading && items.length === 0;

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchInput(v);
    debouncedSetSearch(v.trim());
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
       
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: { xs: "2vh 4vw 2vh 4vw", md: "4vh 2vw 2vh 2vw" },
          background: {
            xs: "none",
            md: "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          },
        }}
      >
        <StyledHeading sx={{ fontSize: { xs: "22px", md: "24px" } }}>
          Customers
        </StyledHeading>
      </Box>

      <Box
        sx={{
          padding:  { xs: "0 4vw 1vh 4vw", md: "4vh 2vw 2vh 2vw" },
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          placeholder="Search customers…"
          value={searchInput}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9CA3AF", fontSize: 18 }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "10px",
              backgroundColor: "#fff",
              fontSize: 14,
              fontFamily: "Inter Tight",
              minWidth: 280,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          padding: "1vh 2vw 0 2vw",
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : showEmpty ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              maxWidth: 480,
              margin: "4vh auto 0",
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: "#949daa",
                fontFamily: "Inter Tight",
              }}
            >
              No customers found
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{padding: "0 1vw"}}>
            {items.map((c, idx) => {
              const fullName =
                c?.username ||
                [c?.first_name, c?.middle_name, c?.last_name]
                  .filter(Boolean)
                  .join(" ")
                  .trim() ||
                "—";
              const email = c?.email || c?.customer_email || "—";
              const phone =
                [c?.phone_code, c?.phone_no].filter(Boolean).join(" ") ||
                c?.phone ||
                "—";
              const pill = pillFor(fullName);
              const proposalsCount =
                c?.proposals_count ??
                c?.no_of_proposals ??
                c?.proposalsCount ??
                null;
              const ongoingCount =
                c?.ongoing_projects ??
                c?.ongoing_projects_count ??
                c?.ongoingProjects ??
                null;
              const projectValue =
                c?.project_value ?? c?.projectValue ?? null;
              return (
                <Card
                  key={c?.id || c?.customer_id || idx}
                  sx={{
                    margin: "0 !important",
                    borderRadius: 0,
                    boxShadow: "none",
                    borderBottom: "1px solid #D7D8D9",
                  }}
                >
                  <CardContent
                    sx={{
                      padding: "16px !important",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "999px",
                          backgroundColor: pill.bg,
                          color: pill.fg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          fontSize: 13,
                          fontFamily: "Inter Tight",
                          flexShrink: 0,
                        }}
                      >
                        {pill.letter}
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#949DAA",
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                          }}
                        >
                          Customer
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#0D0D0E",
                            fontFamily: "Inter Tight",
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fullName}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#949DAA",
                          fontFamily: "Inter Tight",
                          fontWeight: 500,
                          mb: 0.25,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#0D0D0E",
                          fontFamily: "Inter Tight",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {email}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#949DAA",
                          fontFamily: "Inter Tight",
                          fontWeight: 500,
                          mb: 0.25,
                        }}
                      >
                        Phone
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#0D0D0E",
                          fontFamily: "Inter Tight",
                          fontWeight: 500,
                        }}
                      >
                        {phone === "—" ? "—" : `+${phone}`}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#949DAA",
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                            mb: 0.25,
                          }}
                        >
                          No. of Proposals
                        </Typography>
                        <CountPill value={proposalsCount} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#949DAA",
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                            mb: 0.25,
                          }}
                        >
                          Won vs Sent
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#9CA3AF",
                            fontFamily: "Inter Tight",
                          }}
                        >
                          -
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#949DAA",
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                            mb: 0.25,
                          }}
                        >
                          Ongoing Projects
                        </Typography>
                        <CountPill value={ongoingCount} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#949DAA",
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                            mb: 0.25,
                          }}
                        >
                          Project Value
                        </Typography>
                        <ProjectValueCell value={projectValue} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <TableContainer sx={{ boxShadow: "none", backgroundColor: "transparent" }}>
            <Table
              sx={{
                "& thead th": {
                  color: "#949DAA",
                  fontWeight: 500,
                  fontSize: 13,
                },
                "& tbody tr": {
                  borderBottom: "none",
                },
                "& tbody tr:hover": {
                  backgroundColor: "#F7F7F7",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Customer</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Phone</StyledTableCell>
                  <StyledTableCell align="center">No. of Proposals</StyledTableCell>
                  <StyledTableCell align="center">Won vs Sent</StyledTableCell>
                  <StyledTableCell align="center">Ongoing Projects</StyledTableCell>
                  <StyledTableCell align="right">Project Value</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((c, idx) => {
                  const fullName =
                    c?.username ||
                    [c?.first_name, c?.middle_name, c?.last_name]
                      .filter(Boolean)
                      .join(" ")
                      .trim() ||
                    "—";
                  const email = c?.email || c?.customer_email || "—";
                  const phone =
                    [c?.phone_code, c?.phone_no].filter(Boolean).join(" ") ||
                    c?.phone ||
                    "—";
                  const pill = pillFor(fullName);
                  const proposalsCount =
                    c?.proposals_count ??
                    c?.no_of_proposals ??
                    c?.proposalsCount ??
                    null;
                  const ongoingCount =
                    c?.ongoing_projects ??
                    c?.ongoing_projects_count ??
                    c?.ongoingProjects ??
                    null;
                  const projectValue =
                    c?.project_value ?? c?.projectValue ?? null;
                  return (
                    <TableRow key={c?.id || c?.customer_id || idx}>
                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "999px",
                              backgroundColor: pill.bg,
                              color: pill.fg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 600,
                              fontSize: 13,
                              fontFamily: "Inter Tight",
                              flexShrink: 0,
                            }}
                          >
                            {pill.letter}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: "#0D0D0E",
                              fontFamily: "Inter Tight",
                              fontWeight: 500,
                            }}
                          >
                            {fullName}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell sx={{ color: "#0D0D0E", fontSize: 13 }}>
                        {email}
                      </StyledTableCell>
                      <StyledTableCell sx={{ color: "#0D0D0E", fontSize: 13 }}>
                        +{phone}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <CountPill value={proposalsCount} />
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        sx={{ color: "#9CA3AF", fontSize: 13 }}
                      >
                        -
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <CountPill value={ongoingCount} />
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <ProjectValueCell value={projectValue} />
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "2vh",
        }}
      >
        
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          disabled={loading}
        />
      </Box>
    </Box>
  );
};

export default Customers;
